import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Spinner, Badge, Button, Form, InputGroup, Dropdown } from 'react-bootstrap';
import { fetchPublicClasses, setClassFilter } from '../../store/slices/classesSlice';
import { fetchProfile } from '../../store/slices/profileSlice';
import { AuthModal } from '../../components/AuthModal';
import { SEO } from '../../components/SEO';
import './classes.scss';

// ── Fallback image ──
const CLASS_FALLBACK = 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=500&h=280&fit=crop';

const ClassCard = ({ cls, onBook, isLoading }) => {
  const navigate = useNavigate();

  const localStart = cls.start_date ? new Date(cls.start_date) : null;
  const localEnd = cls.end_date ? new Date(cls.end_date) : null;

  const startDate = localStart && !isNaN(localStart.getTime())
    ? localStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'TBD';

  const startTime = localStart && !isNaN(localStart.getTime())
    ? localStart.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';

  const endTime = localEnd && !isNaN(localEnd.getTime())
    ? localEnd.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';

  const timezone = cls.timezone || (Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');

  const handleClick = () => {
    navigate(`/class-detail/${cls._id || cls.id}`);
  };

  const isObjectId = (val) => typeof val === 'string' && /^[0-9a-fA-F]{24}$/.test(val);
  const languageName = (cls.language && typeof cls.language === 'object')
    ? cls.language.name
    : (cls.language && !isObjectId(cls.language) ? cls.language : 'English');

  const instructor = cls.instructor || cls.teacher;
  const teacherName = (instructor && typeof instructor === 'object' && instructor.full_name)
    ? instructor.full_name
    : 'Yoospeak Instructor';
  const teacherId = (instructor && typeof instructor === 'object' && instructor._id)
    ? instructor._id
    : (typeof instructor === 'string' ? instructor : '');
  const teacherAvatar = (instructor && typeof instructor === 'object' && instructor.profile_picture)
    ? instructor.profile_picture
    : 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=40&h=40&fit=crop';

  const communityName = cls.community?.name || cls.community_name || cls.communityName || '';
  const isLive = cls.status === 'live' || cls.is_live === true || cls.started === true;
  const isRecurring = cls.is_recurring === true || cls.recurring === true || cls.type === 'recurring';
  const maxStudents = cls.max_students || cls.seats || 20;
  const enrolledCount = Array.isArray(cls.students) ? cls.students.length : (cls.enrolled_count || 0);
  const seatsLeft = Math.max(0, maxStudents - enrolledCount);

  return (
    <div className="community-card card-hover bg-white border h-100 rounded-4 overflow-hidden pointer shadow-sm transition-all" onClick={handleClick}>
      <div className="position-relative" style={{ height: "180px" }}>
        <img
          src={cls.cover_image || cls.image || CLASS_FALLBACK}
          alt={cls.title}
          className="img-fluid w-100 h-100 object-fit-cover"
          onError={(e) => { e.target.src = CLASS_FALLBACK; }}
        />

        {/* Top Badges */}
        <div className="position-absolute top-0 start-0 m-2 d-flex flex-wrap gap-1 z-3">
          {isLive && (
            <Badge bg="danger" className="rounded-pill px-2 py-1 text-xs fw-bold shadow-sm d-flex align-items-center gap-1">
              <span className="spinner-grow spinner-grow-sm text-white" style={{ width: '6px', height: '6px' }}></span>
              LIVE
            </Badge>
          )}
          {isRecurring && (
            <Badge bg="info" className="rounded-pill px-2 py-1 text-xs fw-bold shadow-sm text-dark">
              🔄 Recurring
            </Badge>
          )}
        </div>

        {/* Price / Free Badge */}
        <div className="position-absolute top-0 end-0 m-2 z-3">
          {cls.cost > 0 ? (
            <Badge bg="dark" className="rounded-pill px-2 py-1 text-xs fw-bold shadow-sm">
              ${cls.cost}
            </Badge>
          ) : (
            <Badge bg="success" className="rounded-pill px-2 py-1 text-xs fw-bold shadow-sm">
              Free
            </Badge>
          )}
        </div>

        {/* Seats Badge */}
        <div className="position-absolute bottom-0 start-0 m-2 z-3">
          <span className="bg-dark bg-opacity-75 text-white rounded-pill px-2 py-1 text-xs fw-semibold">
            <i className="bi bi-people me-1"></i>{seatsLeft} Seats Remaining
          </span>
        </div>
      </div>

      <div className="item-info p-3 d-flex flex-column justify-content-between flex-grow-1">
        <div>
          {/* Instructor & Community Info */}
          <div className="d-flex align-items-center justify-content-between mb-2">
            {instructor && (
              <Link
                to={teacherId ? `/teachers/${teacherId}` : '#'}
                className="card-owner-row d-flex align-items-center gap-2 text-decoration-none"
                onClick={(e) => {
                  if (!teacherId) e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <div className="owner-avatar rounded-circle overflow-hidden border border-light-subtle shadow-sm" style={{ width: '28px', height: '28px', flexShrink: 0 }}>
                  <img
                    src={teacherAvatar}
                    alt={teacherName}
                    className="w-100 h-100 object-fit-cover"
                  />
                </div>
                <span className="owner-name text-xs text-gray-700 fw-semibold text-truncate d-flex align-items-center gap-1">
                  {teacherName}
                  {instructor?.is_blue_verified && <i className="bi bi-patch-check-fill text-primary" style={{ fontSize: '0.75rem' }} title="Verified Teacher" />}
                </span>
              </Link>
            )}
            {communityName && (
              <span className="text-xs text-purple fw-semibold text-truncate ms-auto">
                <i className="bi bi-people-fill me-1"></i>{communityName}
              </span>
            )}
          </div>

          {/* Title */}
          <h5 className="card-title fw-bold text-navy text-truncate-2 mb-2" title={cls.title || 'Untitled Class'}>
            {cls.title || 'Untitled Class'}
          </h5>

          {/* Description */}
          {cls.description && (
            <p className="card-description text-gray-600 text-xs mb-3 text-truncate-2">
              {cls.description}
            </p>
          )}

          {/* Meta tags */}
          <div className="card-meta-row d-flex flex-wrap align-items-center gap-2 mb-3 text-xs text-gray-600">
            <span><i className="bi bi-calendar3 text-primary me-1"></i>{startDate}</span>
            {startTime && <span><i className="bi bi-clock text-primary me-1"></i>{startTime} - {endTime} ({timezone})</span>}
            <span><i className="bi bi-translate text-primary me-1"></i>{languageName}</span>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="card-footer-action pt-2 border-top d-flex align-items-center justify-content-between gap-2">
          <Button
            variant="outline-purple"
            size="sm"
            className="rounded-pill px-3 py-1 text-xs fw-bold"
            onClick={(e) => { e.stopPropagation(); handleClick(); }}
          >
            View Details
          </Button>

          <Button
            variant="purple"
            size="sm"
            className="text-white rounded-pill px-3 py-1 text-xs fw-bold border-0"
            onClick={(e) => { e.stopPropagation(); onBook(cls._id || cls.id); }}
            disabled={isLoading}
          >
            {isLoading ? 'Booking...' : (cls.cost > 0 ? `Join ($${cls.cost})` : 'Join Free')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const ClassesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const teacherIdParam = searchParams.get('teacherId');

  const { classes, loading, error, filters } = useSelector((state) => state.classes);
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { profile, loading: profileLoading, fetched: profileFetched, error: profileError } = useSelector((state) => state.profile);
  const [searchInput, setSearchInput] = useState('');
  const [bookingId, setBookingId] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authAction, setAuthAction] = useState('register for this live class');

  // Additional filter states
  const [selectedFormat, setSelectedFormat] = useState('All'); // 'All', 'Free', 'Paid', 'Live', 'Recurring'
  const [selectedLanguage, setSelectedLanguage] = useState('All');

  useEffect(() => {
    dispatch(fetchPublicClasses());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated && !profile && !profileLoading?.profile && !profileFetched && !profileError?.profile) {
      dispatch(fetchProfile());
    }
  }, [isAuthenticated, profile, profileLoading, profileFetched, profileError, dispatch]);

  const isTeacherOrAdmin = isAuthenticated && profile && (profile.user_type === 'teacher' || profile.user_type === 'admin' || profile.is_admin);

  const handleCreateClassClick = () => {
    if (!isAuthenticated) {
      setAuthAction('create a new live class');
      setShowAuthModal(true);
      return;
    }
    navigate('/create-class');
  };

  // Client-side filtering
  const filtered = classes.filter((cls) => {
    const matchSearch = !filters.search ||
      cls.title?.toLowerCase().includes(filters.search.toLowerCase()) ||
      cls.description?.toLowerCase().includes(filters.search.toLowerCase()) ||
      (cls.instructor?.full_name || cls.teacher?.full_name)?.toLowerCase().includes(filters.search.toLowerCase()) ||
      (cls.community?.name || cls.community_name)?.toLowerCase().includes(filters.search.toLowerCase());

    const instructor = cls.instructor || cls.teacher;
    const instructorId = (instructor && typeof instructor === 'object')
      ? (instructor._id || instructor.id)
      : instructor;
    const actualTeacherId = cls.teacher_id || instructorId;
    const matchTeacher = !teacherIdParam ||
      (actualTeacherId && actualTeacherId.toString() === teacherIdParam.toString());

    let matchFormat = true;
    if (selectedFormat === 'Free') matchFormat = (!cls.cost || Number(cls.cost) === 0);
    if (selectedFormat === 'Paid') matchFormat = (cls.cost && Number(cls.cost) > 0);
    if (selectedFormat === 'Live') matchFormat = (cls.status === 'live' || cls.is_live === true);
    if (selectedFormat === 'Recurring') matchFormat = (cls.is_recurring === true || cls.recurring === true);

    let matchLanguage = true;
    if (selectedLanguage !== 'All') {
      const langName = typeof cls.language === 'object' ? cls.language?.name : (cls.language || '');
      matchLanguage = langName.toLowerCase().includes(selectedLanguage.toLowerCase());
    }

    return matchSearch && matchTeacher && matchFormat && matchLanguage;
  });

  const handleSearch = (e) => {
    e.preventDefault();
    dispatch(setClassFilter({ key: 'search', value: searchInput }));
  };

  const handleBook = (classId) => {
    if (!isAuthenticated) {
      setAuthAction('join this live class');
      setShowAuthModal(true);
      return;
    }
    setBookingId(classId);
    setTimeout(() => setBookingId(null), 1500);
  };

  return (
    <div className="communities-page">
      <SEO title="Live Classes" description="Explore live classes, group lessons, and 1-on-1 tutoring sessions on YooSpeak." />
      <AuthModal show={showAuthModal} onHide={() => setShowAuthModal(false)} title="Continue Learning" actionText={authAction} />

      {/* Hero Section */}
      <section className="hero-banner py-5">
        <Container fluid="xl">
          <Row className="g-4">
            <Col lg={5} md={6}>
              <div className="content-wrapper">
                <div className="hero-heading mb-3">
                  Explore <span className="highlight-purple">Live Classes</span>
                </div>
                <div className="hero-subtitle mb-3">
                  Join expert-led live sessions — 1-on-1 or group formats available. Book a class and start speaking with confidence.
                </div>
                {isTeacherOrAdmin && (
                  <Button variant="purple" className="btn-create-community rounded-pill btn-custom" onClick={handleCreateClassClick}>
                    <i className="bi bi-plus-lg"></i> Create Class
                  </Button>
                )}
              </div>
            </Col>

            <Col lg={7} md={6}>
              <Row className="hero-features-row g-2 g-lg-3">
                <Col lg={6} xl={4}>
                  <div className="hero-feature-card d-flex align-items-start gap-3">
                    <div className="feature-icon rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', flexShrink: 0 }}>
                      <i className="bi bi-camera-video-fill fs-4"></i>
                    </div>
                    <div>
                      <div className="base-heading">Live Sessions</div>
                      <div className="feature-desc text-muted">Join live video calls with expert tutors.</div>
                    </div>
                  </div>
                </Col>
                <Col lg={6} xl={4}>
                  <div className="hero-feature-card d-flex align-items-start gap-3">
                    <div className="feature-icon rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', flexShrink: 0 }}>
                      <i className="bi bi-person-fill fs-4"></i>
                    </div>
                    <div>
                      <div className="base-heading">1-on-1 Tutors</div>
                      <div className="feature-desc text-muted">Get customized focus and personal feedback.</div>
                    </div>
                  </div>
                </Col>
                <Col xl={4}>
                  <div className="hero-feature-card d-flex align-items-start gap-3">
                    <div className="feature-icon rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', flexShrink: 0 }}>
                      <i className="bi bi-people-fill fs-4"></i>
                    </div>
                    <div>
                      <div className="base-heading">Group Lessons</div>
                      <div className="feature-desc text-muted">Practice conversation with peers globally.</div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Filter and Search Bar Section */}
      <section className="categories-section bg-white section-padding pb-0">
        <Container fluid="xl">
          {/* Search bar inside content */}
          <div className="search-box-wrapper mx-auto mb-4" style={{ maxWidth: "600px" }}>
            <Form onSubmit={handleSearch}>
              <InputGroup className="bg-white border rounded-pill shadow-sm overflow-hidden px-2 py-1">
                <InputGroup.Text className="bg-transparent border-0 shadow-none text-gray-700">
                  <i className="bi bi-search"></i>
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search classes by title, instructor or community..."
                  className="border-0 shadow-none px-2"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                {searchInput && (
                  <InputGroup.Text className="bg-transparent border-0 shadow-none text-gray-700">
                    <i className="bi bi-x-circle clear-icon pointer" onClick={() => {
                      setSearchInput('');
                      dispatch(setClassFilter({ key: 'search', value: '' }));
                    }}></i>
                  </InputGroup.Text>
                )}
              </InputGroup>
            </Form>
          </div>

          {/* Filter Pills */}
          <div className="categories-filter-bar mb-4">
            <div className="d-flex flex-wrap justify-content-center gap-2">
              {['All', 'Free', 'Paid', 'Live', 'Recurring'].map((fmt) => (
                <button
                  key={fmt}
                  onClick={() => setSelectedFormat(fmt)}
                  className={`btn btn-sm rounded-pill px-3 py-2 fw-semibold ${selectedFormat === fmt ? 'btn-purple text-white' : 'btn-outline-secondary'}`}
                  style={{ cursor: 'pointer' }}
                >
                  {fmt === 'Live' ? '🔴 Live' : fmt === 'Recurring' ? '🔄 Recurring' : fmt}
                </button>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* Classes Grid Section */}
      <section className="communities-grid-section bg-white section-padding pt-0">
        <Container fluid="xl">
          {/* Results count */}
          {!loading.list && (
            <div className="results-count mb-4 text-gray-600 fw-medium">
              <i className="bi bi-grid-3x3-gap me-2 text-primary"></i>
              {filtered.length} class{filtered.length !== 1 ? 'es' : ''} available
            </div>
          )}

          {/* Loading */}
          {loading.list && (
            <div className="d-flex justify-content-center align-items-center py-5">
              <div className="text-center">
                <Spinner animation="border" variant="primary" className="loading-spinner" />
                <p className="mt-3 text-gray-500 fw-medium">Loading classes...</p>
              </div>
            </div>
          )}

          {/* Error */}
          {error.list && !loading.list && (
            <div className="alert alert-danger rounded-3 text-center py-4">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {error.list}
              <div className="mt-3">
                <Button variant="outline-danger" size="sm" onClick={() => dispatch(fetchPublicClasses())}>
                  Try Again
                </Button>
              </div>
            </div>
          )}

          {/* Empty state */}
          {!loading.list && !error.list && filtered.length === 0 && (
            <div className="empty-state text-center py-5">
              <div className="empty-icon mb-4">
                <i className="bi bi-camera-video-off text-primary empty-state-icon"></i>
              </div>
              <h3 className="fw-bold text-gray-900 mb-2">No classes found</h3>
              <p className="text-gray-500 mb-4">Try adjusting your filters or search terms.</p>
              <Button variant="primary" onClick={() => {
                setSearchInput('');
                setSelectedFormat('All');
                dispatch(setClassFilter({ key: 'search', value: '' }));
                dispatch(setClassFilter({ key: 'type', value: '' }));
              }}>Clear Filters</Button>
            </div>
          )}

          {/* Grid */}
          {!loading.list && filtered.length > 0 && (
            <Row className="g-4 row-cols-1 row-cols-md-2 row-cols-xl-3">
              {filtered.map((cls) => (
                <Col key={cls._id || cls.id}>
                  <ClassCard
                    cls={cls}
                    onBook={handleBook}
                    isLoading={bookingId === (cls._id || cls.id)}
                  />
                </Col>
              ))}
            </Row>
          )}
        </Container>
      </section>

      {/* CTA — App Banner */}
      <div className="app-cta-banner py-5">
        <Container fluid="xl">
          <div className="cta-inner text-center">
            <h2 className="fw-black text-white mb-2">Want more options?</h2>
            <p className="text-white text-opacity-75 mb-4">
              Browse exclusive 1-on-1 sessions and book directly in the Yoospeak app.
            </p>
            <div className="d-flex flex-wrap justify-content-center gap-3">
              <a
                href="https://play.google.com/store/apps/details?id=com.yoospeak.learning"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={require('../../assets/images/play-store.png')}
                  alt="Google Play"
                  className="img-fluid rounded-3 app-store-badge"
                />
              </a>
              <a
                href="https://apps.apple.com/in/app/yoospeak/id6746839854"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={require('../../assets/images/app-store.png')}
                  alt="App Store"
                  className="img-fluid rounded-3 app-store-badge"
                />
              </a>
            </div>
          </div>
        </Container>
      </div>
    </div>
  );
};
